import React, { useState, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import JumbledSentenceExercise from './JumbledSentenceExercise';
import './JumbledSentenceEmbed.css';
import { useExerciseSync } from '../../context/ExerciseSyncContext';

const JumbledSentenceEmbed = ({ node, updateAttributes, editor, getPos }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const exerciseIdRef = React.useRef(node.attrs.exerciseId);
  if (!exerciseIdRef.current) exerciseIdRef.current = crypto.randomUUID();
  const exerciseId = exerciseIdRef.current;

  useEffect(() => {
    if (!node.attrs.exerciseId) updateAttributes({ exerciseId });
  }, []); // eslint-disable-line

  const { myState, otherState, updateState, liveUpdateCount } = useExerciseSync(exerciseId);

  useEffect(() => {
    if (!editor) return;
    const id = requestAnimationFrame(() => {
      try {
        editor.view.updateState(editor.view.state);
        // Mobile Safari/Chrome: re-syncing the view can leave the browser
        // thinking the (still logically-selected) editable region wants the
        // on-screen keyboard, even though the user only tapped this
        // exercise's own contentEditable=false UI. Blur ONLY if the outer
        // editor root itself still has focus (not an input inside the
        // exercise's own edit mode), so the keyboard does not pop on
        // expand/collapse.
        if (typeof document !== 'undefined' && document.activeElement === editor.view.dom) {
          editor.view.dom.blur();
        }
      } catch (_) {}
    });
    return () => cancelAnimationFrame(id);
  }, [isExpanded]); // eslint-disable-line

  // Mirror a REMOTE peer's expand/collapse live, without replaying stale
  // localStorage-hydrated data on every remount — see ExerciseSyncContext's
  // liveUpdateCount for why this can't just watch otherState.expanded alone.
  const lastLiveUpdateCountRef = React.useRef(liveUpdateCount);
  useEffect(() => {
    if (liveUpdateCount === lastLiveUpdateCountRef.current) return;
    lastLiveUpdateCountRef.current = liveUpdateCount;
    if (otherState.expanded !== undefined && otherState.expanded !== isExpanded) {
      setIsExpanded(otherState.expanded);
    }
  }, [liveUpdateCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const { sentences, title, level } = node.attrs;

  const isStudent = (() => {
    try {
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-student-mode');
      if (fromRoot) return fromRoot === 'true';
      return typeof document !== 'undefined' &&
        (window.location.pathname.includes('/student-dashboard') ||
         window.location.pathname.includes('/student-profile'));
    } catch (_) { return false; }
  })();

  const isHomework = (() => {
    try {
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-tab-type');
      if (fromRoot) return fromRoot === 'homework';
      const fromBody = typeof document !== 'undefined' ? document.body.getAttribute('data-novas-current-tab-type') : null;
      return fromBody === 'homework';
    } catch (_) { return false; }
  })();

  if (!sentences || !Array.isArray(sentences) || sentences.length === 0) {
    return (
      <NodeViewWrapper>
        <div className="js-embed-error"><p>Error: Invalid Word Order exercise data</p></div>
      </NodeViewWrapper>
    );
  }

  const handleDragStart = (e) => {
    try {
      e.target.style.opacity = '0.5';
      e.target.classList.add('dragging');
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'copyMove';
        const payload = { type: 'jumbledSentence', attrs: { sentences, title, level } };
        try { e.dataTransfer.setData('application/x-novas-node', JSON.stringify(payload)); } catch (_) {}
      }
    } catch (_) {}
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '';
    e.target.classList.remove('dragging');
  };

  const handleToggle = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    updateState({ expanded: next });
  };

  const handleClose = () => {
    setIsExpanded(false);
    updateState({ expanded: false });
  };

  // Allow placing cursor above/below the embed (like the other exercises)
  const insertParagraphAt = (pos) => {
    if (!editor || typeof pos !== 'number') return;
    editor
      .chain()
      .focus()
      .insertContentAt(pos, { type: 'paragraph' })
      .setTextSelection(pos + 1)
      .run();
  };

  const handleInsertAbove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = typeof getPos === 'function' ? getPos() : null;
    insertParagraphAt(pos);
  };

  const handleInsertBelow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const base = typeof getPos === 'function' ? getPos() : null;
    if (typeof base === 'number' && node && typeof node.nodeSize === 'number') {
      insertParagraphAt(base + node.nodeSize);
    }
  };

  // Only mark the outer wrapper / preview as a drag handle when the embed
  // is collapsed. With the expanded view also flagged as data-drag-handle,
  // ProseMirror treats clicks inside it (e.g. the Close button) as
  // node-selection / drag-start gestures. Same fix as the other exercise
  // embeds.
  const dragProps = isExpanded
    ? {}
    : { draggable: true, 'data-drag-handle': '', onDragStart: handleDragStart, onDragEnd: handleDragEnd };

  return (
    <NodeViewWrapper as="div" className="js-node-view" contentEditable={false} {...dragProps}>
      <div className="embed-insert-zone top" onMouseDown={handleInsertAbove} />
      <div className="js-embed" {...dragProps}>
        {!isExpanded ? (
          <div className="js-embed-preview" onClick={handleToggle} {...dragProps}>
            <div className="js-embed-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/>
                <polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/>
                <line x1="4" y1="4" x2="9" y2="9"/>
              </svg>
            </div>
            <div className="js-unified-preview-main">
              <div className="js-unified-preview-title">{title || 'Word Order Exercise'}</div>
              <div className="js-unified-preview-meta">
                {sentences.length} sentence{sentences.length !== 1 ? 's' : ''} · Level: {level || 'B1'}
              </div>
            </div>
            <div className="js-unified-preview-actions" onClick={e => e.stopPropagation()}>
              <button
                className="js-unified-icon-btn"
                title="Add to homework"
                style={{ display: (isHomework || isStudent) ? 'none' : 'inline-flex' }}
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent('novas:add-to-homework', {
                      detail: { type: 'jumbledSentence', attrs: { sentences, title, level } }
                    }));
                  } catch (_) {}
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L2 8.207V13.5A1.5 1.5 0 0 0 3.5 15h4a.5.5 0 1 0 0-1h-4a.5.5 0 0 1-.5-.5V7.207l5-5 6.646 6.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293z"/>
                  <path d="M16 12.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0m-3.5-2a.5.5 0 0 0-.5.5v1h-1a.5.5 0 0 0 0 1h1v1a.5.5 0 1 0 1 0v-1h1a.5.5 0 1 0 0-1h-1v-1a.5.5 0 0 0-.5-.5"/>
                </svg>
              </button>
              <button
                className="js-unified-icon-btn"
                title="Delete"
                style={{ display: isStudent ? 'none' : 'inline-flex' }}
                onClick={() => editor?.chain().focus().deleteRange({ from: getPos(), to: getPos() + node.nodeSize }).run()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0"/>
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="js-embed-expanded">
            <JumbledSentenceExercise
              sentences={sentences}
              title={title}
              level={level}
              onClose={handleClose}
              updateAttributes={updateAttributes}
              isStudent={isStudent}
              myState={myState}
              otherState={otherState}
              updateState={updateState}
            />
          </div>
        )}
      </div>
      <div className="embed-insert-zone bottom" onMouseDown={handleInsertBelow} />
    </NodeViewWrapper>
  );
};

export default JumbledSentenceEmbed;
